// PanelBrickmould.js
// Brickmould section for Steel Doors.
// Owns its controlMap; bubbles up the full section data via onChange(sectionData).
// All show/hide dependencies are driven by React state — no DOM manipulation.

import React, { useState, useEffect, useRef } from 'react';
import ExpandableSection from '../../../expandablesection';

import Brickmould       from '../../controls/brickmould/Brickmould';
import BrickmouldType   from '../../controls/brickmould/BrickmouldType';
import BrickmouldSize   from '../../controls/brickmould/BrickmouldSize';
import BrickmouldWidth  from '../../controls/brickmould/BrickmouldWidth';
import BrickmouldHeight from '../../controls/brickmould/BrickmouldHeight';

const ev = { value: '', label: '' };

const PanelBrickmould = ({
  data         = {},
  onChange,
  disabled     = false,
  sectionId    = 'Brickmould',
  state,
  onExpandChange,
  forceExpanded,
  frameWidth,   // passed from sd.js — needed by BrickmouldWidth validation
  frameHeight,  // passed from sd.js — needed by BrickmouldHeight validation
}) => {

  // ── section state ─────────────────────────────────────────────────────────
  const [brickmould,       setBrickmould]       = useState(data.brickmould       || ev);
  const [brickmouldType,   setBrickmouldType]   = useState(data.brickmouldType   || ev);
  const [brickmouldSize,   setBrickmouldSize]   = useState(data.brickmouldSize   || ev);
  const [brickmouldWidth,  setBrickmouldWidth]  = useState(data.brickmouldWidth  || ev);
  const [brickmouldHeight, setBrickmouldHeight] = useState(data.brickmouldHeight || ev);

  // ── conditional visibility flags ─────────────────────────────────────────
  const [showTypeSize,   setShowTypeSize]   = useState(false); // Loose or Installed
  const [showDimensions, setShowDimensions] = useState(false); // Installed only

  // ── sync when parent loads saved data (once, when data arrives non-empty) ─
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    if (!data || Object.keys(data).length === 0) return;
    loadedRef.current = true;
    setBrickmould(data.brickmould             || ev);
    setBrickmouldType(data.brickmouldType     || ev);
    setBrickmouldSize(data.brickmouldSize     || ev);
    setBrickmouldWidth(data.brickmouldWidth   || ev);
    setBrickmouldHeight(data.brickmouldHeight || ev);
  }, [data]);

  // ── bubble up — gated by isMounted ref so it never triggers its own re-run ─
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    onChange?.({ brickmould, brickmouldType, brickmouldSize, brickmouldWidth, brickmouldHeight });
  }, [brickmould, brickmouldType, brickmouldSize, brickmouldWidth, brickmouldHeight]);

  // ── section title ─────────────────────────────────────────────────────────
  const title = brickmould?.value && brickmould.value !== 'None'
    ? `🧱 Brickmould  (${brickmould.label})`
    : '🧱 Brickmould';

  // ── controlMap ────────────────────────────────────────────────────────────
  const controlMap = [
    {
      id: 'Brickmould',
      component: (
        <Brickmould
          value={brickmould}
          onChange={setBrickmould}
          disabled={disabled}
          onBrickmouldChildrenVisible={(showTS, showDim) => {
            setShowTypeSize(showTS);
            setShowDimensions(showDim);
          }}
        />
      ),
      position: [1, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    // conditional: shown when Brickmould = Loose or Installed
    ...(showTypeSize ? [
      {
        id: 'BrickmouldType',
        component: (
          <BrickmouldType
            value={brickmouldType}
            onChange={setBrickmouldType}
            disabled={disabled}
          />
        ),
        position: [2, 1],
        status: 'active',
        expandableSectionId: sectionId,
      },
      {
        id: 'BrickmouldSize',
        component: (
          <BrickmouldSize
            value={brickmouldSize}
            onChange={setBrickmouldSize}
            disabled={disabled}
            brickmouldType={brickmouldType}
          />
        ),
        position: [2, 2],
        status: 'active',
        expandableSectionId: sectionId,
      },
    ] : []),
    // conditional: shown only when Brickmould = Installed
    ...(showDimensions ? [
      {
        id: 'BrickmouldWidth',
        component: (
          <BrickmouldWidth
            value={brickmouldWidth}
            onChange={setBrickmouldWidth}
            disabled={disabled}
            frameWidth={frameWidth}
          />
        ),
        position: [3, 1],
        status: 'active',
        expandableSectionId: sectionId,
      },
      {
        id: 'BrickmouldHeight',
        component: (
          <BrickmouldHeight
            value={brickmouldHeight}
            onChange={setBrickmouldHeight}
            disabled={disabled}
            frameHeight={frameHeight}
          />
        ),
        position: [3, 2],
        status: 'active',
        expandableSectionId: sectionId,
      },
    ] : []),
  ];

  return (
    <ExpandableSection
      title={title}
      sectionId={sectionId}
      state={state}
      onExpandChange={onExpandChange}
      forceExpanded={forceExpanded}
      panelLocation="Brickmould"
      controlMap={controlMap.filter(c => c.expandableSectionId === sectionId)}
    />
  );
};

export default PanelBrickmould;
